import { login } from '/@/axios/api/user'
import { getAuth } from '/@/axios/api/auth'
import { getSessionStorage, setSessionStorage, removeSessionStorage } from '/@/utils/storage'
import { changeTree } from '/@/utils/index'
import { routesMap } from '/@/router/routes'
import { encrypt } from '/@/utils/crypto'
import { getUserPlan } from '/@/axios/api/plan'

const getDefaultState = () => {
  return {
    userInfo: null,
    permissions: null,
    permissionsObj: null,
    routes: null
  }
}

const states = getDefaultState()

const mutations = {
  RESET_STATE: state => {
    Object.assign(state, getDefaultState())
  },
  SET_USERINFO: (state, userInfo) => {
    state.userInfo = userInfo
    setSessionStorage('userInfo', userInfo)
  },
  SET_OSS: (state, oss) => {
    state.oss = oss
    setSessionStorage('oss', oss)
  },
  SET_PERMISSIONS: (state, permissions) => {
    state.permissions = permissions
    const permissionsObj = {}
    permissions.forEach(item => {
      permissionsObj[item.href] = item
    })
    state.permissionsObj = permissionsObj
    setSessionStorage('permissions', permissions)
    setSessionStorage('permissionsObj', permissionsObj)
  },
  SET_ROUTES: (state, routes) => {
    state.routes = routes
    setSessionStorage('routes', routes)
  }
}
/**
 * 根据线上权限地址匹配本地可查看菜单
 * 是否显示在菜单列表由权限接口控制
 * @param {本地路由地址} routes
 * @param {线上权限地址} permissions
 */
const getMenuList = (route, permission) => {
  permission.forEach(item => {
    if (route[item.permission] && item.show) {
      item.show = true
      if (item.children && item.children.length > 0) {
        item.showChild = item.children.some(items => {
          return items.show
        })
      }
    } else {
      item.show = false
    }
    item.children && getMenuList(route, item.children)
  })
  permission.sort((a, b) => {
    if (a.sort > b.sort) {
      return -1
    } else if (a.sort < b.sort) {
      return 1
    } else {
      return 0
    }
  })
  return permission
}

// 为超级管理员提供系统管理菜单
const getSuperAdminMenus = () => {
  return [
    { name: '系统设置', href: '/system/config', icon: 'shezhi', show: true, showChild: false },
    { name: '账号管理', href: '/system/account', icon: 'zhanghao', show: true, showChild: false },
    { name: '角色权限', href: '/system/role', icon: 'yonghuziliao-xianxing', show: true, showChild: false },
    { name: '系统菜单', href: '/system/permission', icon: 'caidan', show: true, showChild: false },
    { name: '系统升级', href: '/system/update', icon: 'chanpinguanli', show: true, showChild: false },
    { name: '操作记录', href: '/system/oper', icon: 'jilu', show: true, showChild: false },
    { name: '应用中心', href: '/system/app', icon: 'yingyongguanli', show: true, showChild: false },
    { name: '数据备份', href: '/system/database', icon: 'shuju', show: true, showChild: false },
    { name: '通知管理', href: '/system/notice', icon: 'guangbogonggaotongzhi', show: true, showChild: false },
    { name: '订单管理', href: '/system/payment-order', icon: 'dingdan2', show: true, showChild: false }
  ]
}

const actions = {
  // 登录
  login({ commit }, userInfo) {
    const { username, password, captcha } = userInfo
    return new Promise((resolve, reject) => {
      login({ username: username.trim(), password, captcha }).then(response => {
        if (response.code === 200) {
          const { userInfo, token, permissions, ip, staticIp, oss } = response.data
          // 把用户信息保存到vuex,cookie,方便使用
          commit('SET_USERINFO', userInfo)
          commit('SET_OSS', oss)
          commit('SET_PERMISSIONS', permissions)
          setSessionStorage('token', token)
          // 保存服务器IP信息，供Domain.vue等页面使用
          setSessionStorage('systemConfig', { ip, staticIp })
          resolve({ ip, staticIp })
        } else {
          // 这里直接把完整响应对象抛给调用方，避免只返回 data 导致丢失 message 等信息
          reject(response)
        }
      }).catch(error => {
        reject(error)
      })
    })
  },
  auth({ state, commit, dispatch }, { ip, staticIp }) {
    return new Promise((resolve, reject) => {
      // 授权并写入路由
      getAuth({ ip, staticIp }).then(async response => {
        if (response.code === 200 && response.data) {
          const { authPermissions, plugins, authInfo } = response.data
          setSessionStorage('authInfo', authInfo)
          // 同步用户套餐缓存，供路由守卫使用
          try {
            const planRes = await getUserPlan({}, { loading: false, error: false })
            if (planRes && planRes.data) {
              setSessionStorage('userPlan', planRes.data.plan || null)
            }
          } catch (e) {
            // ignore
          }
          // 判断是否有插件权限，如果有先去掉plugin_，再排序，再加密，再写入缓存
          // 没有插件的ip是不会有zh_plugin的，也打不开属于插件的路由
          if (plugins && plugins.length) {
            const pluginStr = plugins.map(item => {
              return item.replace(/plugin_/g, '')
            })
            pluginStr.sort((a, b) => {
              if (a < b) {
                return -1
              }
              if (a > b) {
                return 1
              }
              return 0
            })
            const zh_plugin = encrypt(pluginStr.join(','))
            setSessionStorage('zh_plugin', zh_plugin)
          }
          // 把插件路由和非插件路由分开
          const permissions = JSON.parse(JSON.stringify(state.permissions))
          const noPluginArr = []
          const pluginArr = []
          const filterPluginArr = []
          // 拆分插件和非插件路由
          permissions.forEach(item => {
            if (item.plugin) {
              pluginArr.push(item)
            } else {
              noPluginArr.push(item)
            }
          })
          // 插件路由与授权路由一致才能添加进页面路由
          pluginArr.forEach(item => {
            authPermissions.forEach(items => {
              if (item.permission === items.permission) {
                filterPluginArr.push(item)
              }
            })
          })
          const newPermissions = [...noPluginArr, ...filterPluginArr]
          let newRoutes

          // 如果是超级管理员，使用特殊的菜单配置
          if (state.userInfo && state.userInfo.role === 'super_admin') {
            newRoutes = getSuperAdminMenus()
          } else {
            newRoutes = getMenuList(routesMap, changeTree(newPermissions))
          }

          commit('SET_ROUTES', newRoutes)
          resolve(true)
        }
      }).catch(err => {
        reject(err)
      })
    })
  },
  getUserInfo({ commit }) {
    return new Promise((resolve, reject) => {
      // 从缓存中获取
      const userInfo = getSessionStorage('userInfo') || null
      const routes = getSessionStorage('routes') || null
      if (userInfo && routes) {
        commit('SET_USERINFO', userInfo)
        commit('SET_ROUTES', routes)
        resolve(userInfo)
      } else {
        reject(false)
      }
    })
  },
  logout(store = {}, reload = true) {
    removeSessionStorage('token')
    removeSessionStorage('userInfo')
    removeSessionStorage('permissions')
    removeSessionStorage('permissionsObj')
    removeSessionStorage('routes')
    removeSessionStorage('routeTag')
    removeSessionStorage('oss')
    removeSessionStorage('zh_plugin')
    removeSessionStorage('system')
    removeSessionStorage('version')
    removeSessionStorage('authInfo')
    removeSessionStorage('userPlan')
    reload && location.reload()
  }
}

export default {
  namespaced: true,
  state: states,
  mutations,
  actions
}
